import { lazy } from 'react'
import { string, bool, ref, array, object, mixed } from 'yup'
import {
    MininumPswLength,
    MaxLengthShortDescription,
    MaxLengthEmailContent,
    EnableOwnerSubscription,
} from './constants'
import i18n from './../i18n'

const Login = lazy(() => import('../pages/login/Login.jsx'))
const LoginEmail = lazy(() => import('../pages/loginEmail/LoginEmail.jsx'))
const Register = lazy(() => import('../pages/register/Register.jsx'))
const RegisterEmail = lazy(() =>
    import('../pages/registerEmail/RegisterEmail.jsx')
)
const Reservations = lazy(() =>
    import('../pages/reservations/Reservations.jsx')
)
const ManageReservations = lazy(() =>
    import('../pages/manageReservations/ManageReservations.jsx')
)
const Profile = lazy(() => import('../pages/profile/Profile.jsx'))
const ProfileEdit = lazy(() => import('../pages/profileEdit/ProfileEdit.jsx'))
const Home = lazy(() => import('../pages/home/Home.jsx'))
const PageNotFound = lazy(() => import('../pages/notFound/NotFound.jsx'))
const CookieLaw = lazy(() => import('../pages/cookieLaw/CookieLaw.jsx'))
const Coworking = lazy(() => import('../pages/coworking/Coworking.jsx'))
const CoworkingManagement = lazy(() =>
    import('../pages/coworkingManagement/CoworkingManagement.jsx')
)
const CoworkingEditInfo = lazy(() =>
    import('../pages/coworkingEditInfo/CoworkingEditInfo.jsx')
)
const CoworkingEditConfig = lazy(() =>
    import('../pages/coworkingEditConfig/CoworkingEditConfig.jsx')
)
const AccountManagement = lazy(() =>
    import('../pages/accountManagement/AccountManagement.jsx')
)
const Version = lazy(() => import('../pages/version/Version.jsx'))
const ReservationsByDate = lazy(() =>
    import('../pages/reservationsByDate/ReservationsByDate')
)
const ReservationResponse = lazy(() =>
    import('../pages/reservationResponse/ReservationResponse')
)

const PrivacyPolicy = lazy(() => import('../pages/privacyPolicy/PrivacyPolicy'))

const CookiesPolicy = lazy(() => import('../pages/cookiesPolicy/CookiePolicy'))

export const routes = [
    {
        path: '/login',
        component: Login,
        visibility: 'unlogged',
        redirect: '/',
        headerActionsMobile: ['back'],
        hasBottomMenu: false,
    },
    {
        path: '/login-email',
        component: LoginEmail,
        visibility: 'unlogged',
        redirect: '/',
        headerActionsMobile: ['back'],
        hasBottomMenu: false,
    },
    {
        path: '/register',
        component: Register,
        visibility: 'unlogged',
        redirect: '/',
        headerActionsMobile: ['back'],
        hasBottomMenu: false,
    },
    {
        path: '/register-email',
        component: RegisterEmail,
        visibility: 'unlogged',
        redirect: '/',
        headerActionsMobile: ['back'],
        hasBottomMenu: false,
    },
    {
        path: '/cookie-law',
        component: CookieLaw,
        visibility: 'all',
        hasBottomMenu: true,
    },
    {
        path: '/',
        component: Home,
        cowokerComponent: Home,
        ownerComponent: ManageReservations,
        ownerHeaderActionsMobile: ['reservationView'],
        ownerHeaderTitle: 'reservations',
        visibility: 'all',
        hasBottomMenu: true,
    },
    {
        path: '/reservations-date',
        ownerComponent: ReservationsByDate,
        visibility: 'logged',
        headerActionsMobile: ['back'],
        hasBottomMenu: true,
    },
    {
        path: '/reservation-response',
        ownerComponent: ReservationResponse,
        visibility: 'logged',
        headerActionsMobile: ['back'],
        hasBottomMenu: true,
    },
    {
        path: '/profile',
        component: Profile,
        visibility: 'logged',
        hasBottomMenu: true,
    },
    {
        path: '/profile-edit',
        component: ProfileEdit,
        visibility: 'logged',
        headerActionsMobile: ['back'],
        headerTitle: 'edit_profile',
        hasBottomMenu: true,
    },
    {
        path: '/reservations',
        coworkerComponent: Reservations,
        visibility: 'logged',
        hasBottomMenu: true,
    },
    {
        path: '/coworking/:id',
        component: Coworking,
        visibility: 'all',
        headerActionsMobile: ['back', 'share', 'favorite'],
        hasBottomMenu: true,
    },
    {
        path: '/coworking-management',
        ownerComponent: CoworkingManagement,
        visibility: 'logged',
        hasBottomMenu: true,
    },
    {
        path: '/coworking-edit-info',
        ownerComponent: CoworkingEditInfo,
        visibility: 'logged',
        headerActionsMobile: ['back'],
        headerTitle: 'edit_informations',
        hasBottomMenu: true,
    },
    {
        path: '/coworking-edit-config',
        ownerComponent: CoworkingEditConfig,
        visibility: 'logged',
        headerActionsMobile: ['back'],
        headerTitle: 'edit_configuration',
        hasBottomMenu: true,
    },
    {
        path: '/accmgmt',
        component: AccountManagement,
        visibility: 'all',
        redirect: '/',
        hasBottomMenu: false,
    },
    {
        path: '/version',
        component: Version,
        visibility: 'all',
        headerActionsMobile: ['back'],
        headerTitle: 'Versione',
        redirect: '/',
        hasBottomMenu: false,
    },
    {
        path: '/terms-and-conditions',
        component: PrivacyPolicy,
        visibility: 'all',
        headerActionsMobile: ['back'],
        headerTitle: 'terms_and_conditions',
        redirect: '/',
        hasBottomMenu: false,
    },
    {
        path: '/cookies-policy',
        component: CookiesPolicy,
        visibility: 'all',
        headerActionsMobile: ['back'],
        headerTitle: 'cookies_policy',
        redirect: '/',
        hasBottomMenu: false,
    },
    {
        path: '*',
        visibility: 'all',
        component: PageNotFound,
        hasBottomMenu: true,
    },
]

export const mainMenu = {
    search: {
        text: 'explore',
        icon: 'search',
        iconAlt: 'search_icon',
        url: '/',
    },
    manageReservations: {
        text: 'reservations',
        icon: 'reservations',
        iconAlt: 'reservations_icon',
        url: '/',
    },
    reservations: {
        text: 'reservations',
        icon: 'reservations',
        iconAlt: 'reservations_icon',
        url: '/reservations',
    },
    profile: {
        text: 'profile',
        icon: 'profile',
        iconAlt: 'profile_icon',
        url: '/profile',
    },
    coworking: {
        text: 'coworking',
        icon: 'coworking',
        iconAlt: 'coworking_icon',
        url: '/coworking-management',
    },
    login: {
        text: 'enter',
        url: '/login',
        icon: 'login',
        iconAlt: 'login_icon',
    },
    register: {
        text: 'register',
        url: '/register',
        icon: 'register',
        iconAlt: 'register_icon',
    },
    registerOwner: {
        text: 'add_coworking',
        url: '/register',
        params: { userType: 'owner' },
    },
}

export function mainMenuRules() {
    let mainMenuRulesObj = {
        coworker: [
            { key: 'search', visibility: ['m', 'd'] },
            { key: 'reservations', visibility: ['m', 'd'] },
            { key: 'profile', visibility: ['m', 'd'] },
        ],
        guest: [
            { key: 'login', visibility: ['m', 'd'] },
            { key: 'register', visibility: ['m', 'd'] },
        ],
        owner: [
            { key: 'manageReservations', visibility: ['m', 'd'] },
            { key: 'coworking', visibility: ['m', 'd'] },
            { key: 'profile', visibility: ['m', 'd'] },
        ],
    }

    if (EnableOwnerSubscription)
        mainMenuRulesObj.guest.push({ key: 'registerOwner', visibility: ['d'] })

    return mainMenuRulesObj
}

export const fieldsSchema = [
    {
        states: [
            'name',
            'surname',
            'fullDescription',
            'feedback',
            'supportRequest',
        ],
        rule: string().required(i18n.t('required_field')),
    },
    {
        states: ['services', 'images'],
        rule: array().required(i18n.t('required_field')),
    },
    {
        states: ['password'],
        rule: string()
            .min(
                MininumPswLength,
                i18n.t('too_short', { minLength: MininumPswLength })
            )
            .matches(/\d/g, i18n.t('contain_number'))
            .matches(/[A-Z]/, i18n.t('contain_uppercase_char'))
            .required(i18n.t('required_field')),
    },
    {
        states: ['confirmPassword'],
        rule: string()
            .min(
                MininumPswLength,
                i18n.t('too_short', { minLength: MininumPswLength })
            )
            .matches(/\d/g, i18n.t('contain_number'))
            .matches(/[A-Z]/, i18n.t('contain_uppercase_char'))
            .required(i18n.t('required_field'))
            .when('password', {
                is: val => val && val.length > 0,
                then: string().oneOf(
                    [ref('password')],
                    i18n.t('passwords_need_same')
                ),
            }),
    },
    {
        states: ['email'],
        rule: string()
            .email(i18n.t('invalid_email'))
            .required(i18n.t('required_field')),
    },
    {
        states: ['privacyCheck'],
        rule: bool()
            .required(i18n.t('required_field'))
            .oneOf([true], i18n.t('required_field')),
    },
    {
        states: ['description'],
        rule: string()
            .required(i18n.t('required_field'))
            .max(
                MaxLengthShortDescription,
                i18n.t('exceeded_max_char', {
                    maxLength: MaxLengthShortDescription,
                })
            ),
    },
    {
        states: ['location'],
        rule: mixed().test('correct', i18n.t('need_street_number'), value =>
            object({
                address: string().required(),
                city: string().required(),
                lat: string().required(),
                lng: string().required(),
                streetNumber: string().required(),
            }).isValid(value)
        ),
    },
    {
        states: ['openingHours'],
        rule: mixed()
            .test('not empty', i18n.t('required_field'), value => {
                return value.length > 0
            })
            .test('correct', i18n.t('required_opening_hours'), value => {
                return (
                    value[0].daysInterval.length === 2 &&
                    value[0].morningHours.close >= 0 &&
                    value[0].morningHours.open >= 0
                )
            }),
    },
    {
        states: ['meetingRooms'],
        rule: mixed().test('not empty', i18n.t('required_field'), value => {
            return value.length > 0
        }),
        // .test('correct', i18n.t('required_opening_hours'), value => {
        //     return value.reduce((acc, val) => {
        //         console.log('val', val)
        //         console.log('acc', acc)
        //         return false
        //         // return (
        //         //     acc &
        //         //     (val.name.trim() !== '' &&
        //         //         val.morningHours.close >= 0 &&
        //         //         val.morningHours.open >= 0)
        //         // )
        //     })
        // }),
    },
    {
        states: ['mailContent'],
        rule: string()
            .required(i18n.t('required_field'))
            .max(
                MaxLengthEmailContent,
                i18n.t('exceeded_max_char', {
                    maxLength: MaxLengthEmailContent,
                })
            ),
    },
]
